// components/Sender/SenderRuntimesTab.js
import React, { useState } from "react";
import { SenderApi } from "./SenderApi";

const KIND_CFG = {
  chrome:  { icon: "🌐", label: "Chrome",  color: "#1d4ed8", bg: "#dbeafe" },
  android: { icon: "📱", label: "Android", color: "#0891b2", bg: "#cffafe" },
};

const CONFIG_FIELDS = {
  chrome: [
    { key: "profile_dir", label: "Profile dir",
      placeholder: "~/.sender/chrome/main" },
    { key: "proxy", label: "Proxy (optional)",
      placeholder: "http://user:pass@host:port" },
    { key: "ws_port", label: "Debug port (optional)",
      placeholder: "9222" },
    { key: "ip_change_url", label: "IP change URL (optional)",
      placeholder: "https://rotate.example.com/change",
      hint: "URL, который будет дёрнут для смены IP." },
    { key: "change_ip_on_new_worker",
      label: "Менять IP при первом использовании нового воркера",
      type: "boolean",
      hint: "Перед первой отправкой нового воркера на этом runtime." },
    { key: "worker_sequential",
      label: "Воркер подряд до лимита (без round-robin)",
      type: "boolean",
      hint: "Один воркер отправляет все свои сообщения до своего лимита, " +
            "затем переключаемся на следующего." },
    { key: "fetch_mode",
      label: "Порядок выборки intentions из API",
      type: "select",
      default: "sequential",
      options: [
        { value: "sequential", label: "📋 по порядку" },
        { value: "random",     label: "🎲 случайно" },
      ],
      hint: "Как вычитывать intentions из API для блоков, " +
            "использующих этот runtime." },
  ],
  android: [
    { key: "serial", label: "ADB serial (optional)",
      placeholder: "emulator-5554 | 192.168.1.10:5555",
      hint: "Пусто → берётся первый device из `adb devices`." },
    { key: "proxy", label: "Proxy (optional)",
      placeholder: "http://user:pass@host:port" },
    { key: "ip_change_url", label: "IP change URL (optional)",
      placeholder: "https://rotate.example.com/change" },
    { key: "change_ip_on_new_worker",
      label: "Менять IP при первом использовании нового воркера",
      type: "boolean" },
    { key: "worker_sequential",
      label: "Воркер подряд до лимита (без round-robin)",
      type: "boolean" },
    { key: "fetch_mode",
      label: "Порядок выборки intentions из API",
      type: "select",
      default: "sequential",
      options: [
        { value: "sequential", label: "📋 по порядку" },
        { value: "random",     label: "🎲 случайно" },
      ],
      hint: "Как вычитывать intentions из API." },
  ],
};

export function SenderRuntimesTab({
  runtimes, setRuntimes, localRuntimes, onRefreshGlobal,
  localOnline, add,
}) {
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const refreshGlobal = async () => {
    try {
      await onRefreshGlobal?.();
    } catch (e) {
      add?.("Не удалось обновить runtimes: " + e.message, "error");
    }
  };

  const localById = {};
  (localRuntimes || []).forEach((lr) => { localById[lr.id] = lr; });

  const save = async (rt) => {
    setBusy(true);
    try {
      const payload = {
        id: rt.id,
        kind: rt.kind,
        config: rt.config || {},
        is_enabled: rt.is_enabled !== false,
      };
      let saved;
      const existsGlobal = runtimes.some((x) => x.id === rt.id);
      if (existsGlobal) {
        saved = await SenderApi.updateRuntime(payload);
        setRuntimes(runtimes.map((x) => (x.id === saved.id ? saved : x)));
      } else {
        saved = await SenderApi.createRuntime(payload);
        setRuntimes([...runtimes, saved]);
      }
      setEditing(null);
      add?.(`💾 Runtime «${saved.id}» сохранён`, "success");
    } catch (e) {
      add?.("Ошибка: " + e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const del = async (rt) => {
    const lr = localById[rt.id];
    if (lr && lr.alive) {
      add?.("Сначала остановите runtime (⏹)", "warn");
      return;
    }
    if (!window.confirm(
      `Удалить runtime «${rt.id}»?\n\n` +
      `Привязки к нему в шаблонах сломаются.`
    )) return;
    try {
      try { await SenderApi.deleteLocalRuntime(rt.id); } catch { /* ignore */ }
      await SenderApi.deleteRuntime(rt.id);
      setRuntimes(runtimes.filter((x) => x.id !== rt.id));
      add?.("🗑 Runtime удалён", "success");
    } catch (e) {
      add?.("Ошибка: " + e.message, "error");
    }
  };

  const toggleEnabled = async (rt) => {
    try {
      const saved = await SenderApi.updateRuntime({
        ...rt, is_enabled: !rt.is_enabled,
      });
      setRuntimes(runtimes.map((x) => (x.id === saved.id ? saved : x)));
    } catch (e) {
      add?.("Ошибка: " + e.message, "error");
    }
  };

  const localStart = async (rt) => {
    if (!localOnline) {
      add?.("Локальный бекенд не запущен", "warn");
      return;
    }
    try {
      await SenderApi.startLocalRuntime(rt.id, rt.kind, rt.config || {});
      add?.(`▶ Runtime «${rt.id}» стартует (daemon подхватит)`, "info");
    } catch (e) {
      add?.("Ошибка старта: " + e.message, "error");
    }
  };

  const localStop = async (rt) => {
    if (!localOnline) return;
    try {
      await SenderApi.stopLocalRuntime(rt.id);
      add?.(`⏹ Runtime «${rt.id}» останавливается`, "info");
    } catch (e) {
      add?.("Ошибка: " + e.message, "error");
    }
  };

  const localHealth = async (rt) => {
    if (!localOnline) {
      add?.("Локальный бекенд не запущен", "warn");
      return;
    }
    try {
      const h = await SenderApi.healthLocalRuntime(rt.id);
      const ok = h.ok ? "✅" : "❌";
      const extra = h.last_error ? ` · ${h.last_error}` : "";
      add?.(`${ok} ${rt.id}: ${h.status}${extra}`, h.ok ? "info" : "warn");
    } catch (e) {
      add?.("Ошибка health: " + e.message, "error");
    }
  };

  return (
    <div>
      <div className="section-card" style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div>
            <h3 style={{ margin: 0 }}>🖥 Runtimes</h3>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              Глобальные метаданные — в Django · Старт/стоп — через локальный
              бекенд. Всего: {runtimes.length}
              {localOnline === false && (
                <span style={{ color: "#b45309" }}>
                  {" "}· ⚠️ локальный бекенд офлайн
                </span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn-cancel" onClick={refreshGlobal}>
              🔄 Обновить
            </button>
            <button className="btn-primary"
                    onClick={() => setEditing({ kind: "chrome" })}>
              ➕ Новый runtime
            </button>
          </div>
        </div>
      </div>

      <div className="section-card">
        {runtimes.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🖥</div>
            <div>Пока нет ни одного runtime.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {runtimes.map((rt) => {
              const cfg = KIND_CFG[rt.kind] || KIND_CFG.chrome;
              const c = rt.config || {};
              const lr = localById[rt.id];
              const alive = !!(lr && lr.alive);
              const ipNote = c.change_ip_on_new_worker && c.ip_change_url
                ? " · 🔄 меняет IP"
                : (c.change_ip_on_new_worker ? " · ⚠️ IP-chg без URL" : "");
              const fm = (c.fetch_mode || "").toLowerCase();
              const fmLabel = fm === "random"
                ? "🎲 random"
                : (fm === "sequential" ? "📋 sequential" : "");
              return (
                <div key={rt.id}
                     style={{
                       border: "1px solid #e5e7eb", borderRadius: 10,
                       padding: 14, background: "#fff",
                       opacity: rt.is_enabled ? 1 : 0.55,
                       display: "flex", alignItems: "center", gap: 14,
                     }}>
                  <div style={{
                    padding: "8px 12px", borderRadius: 8,
                    background: cfg.bg, color: cfg.color,
                    fontSize: 12, fontWeight: 700,
                    minWidth: 90, textAlign: "center",
                  }}>
                    <div style={{ fontSize: 18 }}>{cfg.icon}</div>
                    {cfg.label}
                  </div>

                  <div style={{
                    padding: "6px 10px", borderRadius: 8, fontSize: 11,
                    fontWeight: 700, minWidth: 100, textAlign: "center",
                    background: alive ? "#dcfce7" : "#f3f4f6",
                    color: alive ? "#166534" : "#6b7280",
                  }}>
                    {alive ? "🟢 alive" : "⚪ stopped"}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14,
                                  fontFamily: "Consolas, monospace" }}>
                      {rt.id}
                      {ipNote && (
                        <span style={{ fontSize: 11, fontWeight: 400,
                                       color: c.ip_change_url
                                         ? "#0891b2" : "#b45309",
                                       marginLeft: 8 }}>
                          {ipNote}
                        </span>
                      )}
                      {c.worker_sequential && (
                        <span style={{ fontSize: 11, fontWeight: 700,
                                       color: "#7c3aed",
                                       marginLeft: 8 }}>
                          🔒 sequential
                        </span>
                      )}
                      {fmLabel && (
                        <span style={{ fontSize: 11, fontWeight: 700,
                                       color: "#0891b2",
                                       marginLeft: 8 }}>
                          {fmLabel}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280",
                                  marginTop: 4, display: "flex",
                                  gap: 12, flexWrap: "wrap" }}>
                      {Object.entries(c)
                        .filter(([k]) =>
                          k !== "change_ip_on_new_worker"
                          && k !== "worker_sequential"
                          && k !== "fetch_mode")
                        .map(([k, v]) => (
                          <span key={k}>
                            <span style={{ color: "#9ca3af" }}>{k}:</span>{" "}
                            <b style={{ fontFamily: "monospace" }}>
                              {v === "" || v == null ? "—" : String(v)}
                            </b>
                          </span>
                        ))}
                      {lr && lr.last_error && (
                        <span style={{ color: "#dc2626" }}>
                          ⚠️ {lr.last_error}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 4 }}>
                    {!alive ? (
                      <button className="btn-cancel"
                              style={{ padding: "6px 10px", fontSize: 12,
                                       color: "#16a34a" }}
                              onClick={() => localStart(rt)}
                              disabled={localOnline === false}
                              title="Запустить">
                        ▶
                      </button>
                    ) : (
                      <button className="btn-cancel"
                              style={{ padding: "6px 10px", fontSize: 12,
                                       color: "#dc2626" }}
                              onClick={() => localStop(rt)}
                              disabled={localOnline === false}
                              title="Остановить">
                        ⏹
                      </button>
                    )}
                    <button className="btn-cancel"
                            style={{ padding: "6px 10px", fontSize: 12 }}
                            onClick={() => localHealth(rt)}
                            disabled={localOnline === false}>🔍</button>
                  </div>

                  <button className="btn-cancel"
                          style={{ padding: "6px 10px", fontSize: 12 }}
                          onClick={() => toggleEnabled(rt)}>
                    {rt.is_enabled ? "🚫" : "✅"}
                  </button>
                  <button className="btn-cancel"
                          style={{ padding: "6px 10px", fontSize: 12 }}
                          onClick={() => setEditing(rt)}>✏️</button>
                  <button className="btn-cancel"
                          style={{ padding: "6px 10px", fontSize: 12,
                                   color: "#dc2626" }}
                          onClick={() => del(rt)}>🗑</button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <RuntimeEditor
          initial={editing}
          existingIds={runtimes.map((x) => x.id)}
          busy={busy}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function RuntimeEditor({ initial, existingIds, busy, onSave, onCancel }) {
  const isNew = !existingIds.includes(initial.id);
  const [draft, setDraft] = useState(() => ({
    id: initial.id || "",
    kind: initial.kind || "chrome",
    is_enabled: initial.is_enabled !== false,
    config: { ...(initial.config || {}) },
  }));

  const patch = (p) => setDraft({ ...draft, ...p });
  const patchCfg = (key, val) =>
    patch({ config: { ...draft.config, [key]: val } });

  const cfgFields = CONFIG_FIELDS[draft.kind] || [];

  return (
    <div style={overlay} onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={card}>
        <div style={header}>
          <h3 style={{ margin: 0 }}>
            {isNew ? "➕ Новый runtime" : `✏️ ${initial.id}`}
          </h3>
          <button className="btn-cancel" onClick={onCancel}>✕</button>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: 20,
                      display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid",
                        gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label className="form-label">ID (уникальный)</label>
              <input className="form-input" value={draft.id}
                     disabled={!isNew}
                     onChange={(e) => patch({ id: e.target.value.trim() })} />
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                Например: chrome_main, android_1
              </div>
            </div>
            <div>
              <label className="form-label">Тип</label>
              <select className="form-input" value={draft.kind}
                      onChange={(e) => patch({ kind: e.target.value })}>
                <option value="chrome">🌐 Chrome</option>
                <option value="android">📱 Android</option>
              </select>
            </div>
          </div>

          <div style={{ padding: 14, background: "#f9fafb",
                        borderRadius: 10, border: "1px solid #e5e7eb" }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>
              {draft.kind === "chrome" ? "Настройки Chrome" : "Настройки Android"}
            </div>

            {cfgFields.map((f) => {
              if (f.type === "boolean") {
                const disabled = f.key === "change_ip_on_new_worker"
                  && !draft.config.ip_change_url;
                const on = !!draft.config[f.key];
                return (
                  <label key={f.key}
                         style={{
                           display: "flex", alignItems: "flex-start", gap: 8,
                           fontSize: 13, marginBottom: 12,
                           cursor: disabled ? "not-allowed" : "pointer",
                           opacity: disabled ? 0.5 : 1,
                         }}>
                    <input type="checkbox" checked={on} disabled={disabled}
                           onChange={(e) => patchCfg(f.key, e.target.checked)}
                           style={{ marginTop: 2 }} />
                    <span>
                      <span style={{ fontWeight: on ? 600 : 400 }}>
                        {f.label}
                      </span>
                      {f.hint && (
                        <div style={{ fontSize: 11, color: "#9ca3af",
                                      marginTop: 3 }}>
                          {f.hint}
                        </div>
                      )}
                    </span>
                  </label>
                );
              }
              if (f.type === "select") {
                const val = draft.config[f.key] ?? f.default ?? "";
                return (
                  <div key={f.key} style={{ marginBottom: 10 }}>
                    <label className="form-label">{f.label}</label>
                    <select className="form-input" value={val}
                            onChange={(e) => patchCfg(f.key, e.target.value)}>
                      {(f.options || []).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    {f.hint && (
                      <div style={{ fontSize: 11, color: "#9ca3af",
                                    marginTop: 4 }}>
                        {f.hint}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <div key={f.key} style={{ marginBottom: 10 }}>
                  <label className="form-label">{f.label}</label>
                  <input className="form-input"
                         placeholder={f.placeholder || ""}
                         value={draft.config[f.key] || ""}
                         onChange={(e) => patchCfg(f.key, e.target.value)} />
                  {f.hint && (
                    <div style={{ fontSize: 11, color: "#9ca3af",
                                  marginTop: 4 }}>
                      {f.hint}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8,
                          fontSize: 13 }}>
            <input type="checkbox" checked={draft.is_enabled}
                   onChange={(e) => patch({ is_enabled: e.target.checked })} />
            Включён (можно выбирать в шаблонах)
          </label>
        </div>

        <div style={footer}>
          <button className="btn-cancel" onClick={onCancel}>Отмена</button>
          <button className="btn-primary" disabled={busy}
                  onClick={() => {
                    if (!draft.id) { alert("ID обязателен"); return; }
                    onSave(draft);
                  }}>
            {busy ? "⏳…" : "💾 Сохранить"}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,.6)",
  backdropFilter: "blur(3px)", zIndex: 1200,
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20,
};
const card = {
  background: "#fff", borderRadius: 14, width: "100%",
  maxWidth: 640, maxHeight: "90vh", display: "flex",
  flexDirection: "column", overflow: "hidden",
};
const header = {
  padding: "16px 20px", borderBottom: "1.5px solid #e5e7eb",
  display: "flex", justifyContent: "space-between", alignItems: "center",
};
const footer = {
  padding: "12px 20px", borderTop: "1.5px solid #e5e7eb",
  display: "flex", justifyContent: "flex-end", gap: 8,
};